/**
 * Remembers that the USER has zoomed, so the app stops fighting them.
 *
 * The conflict this resolves: the charts re-fit their axes whenever data arrives
 * — fitAndPinX on /temperature, zoomExtents on /graph, and on /health from the
 * TICK subscription, so there it fired on every tick rather than merely on new
 * data. That re-fit is correct and was itself a fix: without it the x axis
 * drifted off the liner bounds. But it also destroyed any zoom the user had set,
 * within one real-time poll at the latest, which is why zoom appeared to "snap
 * back" and leave the slider stranded.
 *
 * Both behaviours are wanted; they just must not both apply at once. The latch is
 * the discriminator: pin freely until the user takes control, then leave the axes
 * alone until they hand it back.
 *
 * Parity note (viscom, from the desktop source): desktop zoom is PURE
 * CLIENT-SIDE — ZoomPanModifier / RubberBandXyZoomModifier, double-click resets
 * to extents — and it leaves AutoRange off on a zoomed axis. It also accepts that
 * its AutoRange=Always axes simply cannot be zoomed, so our matching those is
 * parity rather than a gap.
 */
export class ZoomLatch {
  private zoomed = false;

  /** True once the user has changed the range themselves. */
  get isZoomed(): boolean {
    return this.zoomed;
  }

  /**
   * Record a range change.
   *
   * `programmatic` distinguishes our own pinning from a real gesture. Getting
   * this wrong in the permissive direction is much worse than the reverse: a
   * programmatic change mistaken for a gesture latches the axes permanently and
   * looks exactly like the pinning bug this replaces.
   */
  noteRangeChange(programmatic: boolean): void {
    if (!programmatic) {
      this.zoomed = true;
    }
  }

  /**
   * Hand control back — zoom-out button, double-click, or a project change.
   *
   * A project change MUST reset: the new project has its own liner bounds, and a
   * range held over from the previous one would frame it wrongly with no way for
   * the user to know why.
   */
  reset(): void {
    this.zoomed = false;
    this.freshData = false;
  }

  // ── returning to live ──────────────────────────────────────────────────────
  //
  // JOEL'S RULING (option 1): hold position while the user is scrubbed, and give
  // them an explicit way back. The holding half already existed — it IS this latch,
  // and it is also Rob's original complaint, "the horizontal slider at the bottom of
  // most graphs will not return you to the latest tick of telemetry". He could scrub
  // back and not get back. So the missing piece was never the holding; it was the
  // way forward again.
  //
  // Option 2 (match desktop: live data yanks the view forward) was rejected because
  // it would mean ADDING forward-yank behaviour web does not have, and it would make
  // replay unusable during the live streaming cure that is the demo.
  //
  // Deliberately NOT a zoom-out: zoomOut() already resets the latch, but it is a
  // zoom control. "Return to live" is a different intent that happens to share a
  // mechanism, and conflating them is why there appeared to be no way back.

  private freshData = false;

  /** True while the view is following the newest data. */
  get isFollowing(): boolean {
    return !this.zoomed;
  }

  /**
   * Data arrived. Only interesting while the user is HELD — that is what lets the
   * affordance say "there is something new back there" rather than just "live".
   *
   * Ignored while following, because then the view already shows it.
   */
  noteDataArrived(): void {
    if (this.zoomed) {
      this.freshData = true;
    }
  }

  /** True when the user is behind AND data has arrived since they fell behind. */
  get hasNewDataBehind(): boolean {
    return this.zoomed && this.freshData;
  }
}
